<template>
  <div>
    <!-- Login screen -->
    <div class="login-container" v-if="!username">
      <div class="login-card">
        <h2><img src="@/assets/logo.png" alt="Maad Logo" class="logo" /></h2>

        <!-- Login form -->
        <form v-if="!resetMode" @submit.prevent="signin">
          <div class="form-group">
            <label for="email">Email</label>
            <input
              v-model="form.email"
              type="email"
              id="email"
              placeholder="Entrez votre email"
              required
            />
          </div>
          <div class="form-group">
            <label for="password">Mot de passe</label>
            <input
              v-model="form.password"
              type="password"
              id="password"
              placeholder="Entrez votre mot de passe"
              required
            />
          </div>
          <div v-if="error" class="error-message">{{ error }}</div>
          <button type="submit" :disabled="loading">
            <span v-if="!loading">Se connecter</span>
            <span v-else>Loading...</span>
          </button>
        </form>

        <!-- Password reset form -->
        <div v-else>
          <div class="form-group">
            <label for="reset-email">Email</label>
            <input
              v-model="form.email"
              type="email"
              id="reset-email"
              placeholder="Entrez votre email"
              required
            />
          </div>
          <div v-if="error" class="error-message">{{ error }}</div>
          <button @click="requestPasswordReset" :disabled="loading">
            <span v-if="!loading">Réinitialiser le mot de passe</span>
            <span v-else>Loading...</span>
          </button>
          <button @click="resetMode = false" class="mt-2" :disabled="loading">
            Retour à la connexion
          </button>
        </div>

        <p v-if="!resetMode" class="forgot-password">
          Mot de passe oublié?
          <a href="#" @click.prevent="resetMode = true">Réinitialiser</a>
        </p>
      </div>
    </div>

    <!-- Snackbar notifications -->
    <v-snackbar
      v-model="snackbar.on"
      :color="snackbar.type"
      top
      :timeout="3000"
    >
      {{ snackbar.text }}
      <v-btn dark text @click="snackbar.on = false">
        {{ $t("default.contact_us.close") }}
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import Api from "@/services/api.js";
import i18n from "@/plugins/i18n";
import bus from "@/services/bus.js";

export default {
  name: "App",
  data() {
    return {
      form: {
        email: "",
        password: "",
      },
      resetMode: false,
      username: null,
      isSuperAdmin: false,
      isBrandPortalUser: false,
      snackbar: {
        on: false,
        type: "",
        text: "",
      },
      loading: false,
      error: "",
    };
  },
  created() {
    this.setLanguage();
    this.forgetStoredPassword();
    this.checkLoggedIn();

    bus.$on("successful_signin", (data) => {
      this.username = (data.username || "").capitalize();
      this.isSuperAdmin = data.isSuperAdmin || data.role_name === "SUPER_ADMIN";
      this.isBrandPortalUser = data.isBrandPortalUser || data.role_name === "PORTAL";

      const userData = Object.assign({}, data, {
        userId: data.id,
        username: (data.username || "").capitalize(),
        password: undefined,
      });
      localStorage.setItem("userData", JSON.stringify(userData));

      if (!this.isSuperAdmin && !this.isBrandPortalUser) {
        Api.bootstrap.get();
        this.openDashboard();
      }
    });

    bus.$on("success", (message) => {
      this.snackbar = {
        on: true,
        type: "success",
        text: message,
      };
    });

    bus.$on("error", (message) => {
      this.snackbar = {
        on: true,
        type: "error",
        text: message,
      };
    });
  },
  methods: {
    // Sign-in answered the password hash until SEC-5, and this page stored the answer: drop the copy still kept.
    forgetStoredPassword() {
      try {
        const stored = JSON.parse(localStorage.getItem("userData"));
        if (stored && "password" in stored) {
          delete stored.password;
          localStorage.setItem("userData", JSON.stringify(stored));
        }
      } catch (e) {
        // Unreadable or no storage: nothing to drop.
      }
    },
    async signin() {
      this.loading = true;
      this.error = "";
      try {
        const res = await Api.users.signin(this.form);
        if (res.status === "OK") {
          const data = res.data;
          this.username = (data.username || "").capitalize();
          this.isSuperAdmin = data.isSuperAdmin || data.role_name === "SUPER_ADMIN";
          this.isBrandPortalUser = data.isBrandPortalUser || data.role_name === "PORTAL";

          const userData = Object.assign({}, data, {
            userId: data.id,
            username: (data.username || "").capitalize(),
            password: undefined,
          });
          localStorage.setItem("userData", JSON.stringify(userData));

          bus.$emit("successful_signin", data);
          bus.$emit("success", this.$t("default.sign_in_success"));

          if (this.isBrandPortalUser) {
            this.openBrandPortal();
          } else if (!this.isSuperAdmin) {
            Api.bootstrap.get();
            this.openDashboard();
          } else {
            this.openAdmin();
          }
        } else {
          throw new Error(this.$t("default.sign_in_error"));
        }
      } catch (err) {
        this.error = err.message || this.$t("default.sign_in_error");
        bus.$emit("error", this.error);
      } finally {
        this.loading = false;
      }
    },
    async requestPasswordReset() {
      this.error = "";
      this.loading = true;

      const email = this.form.email.trim().toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(email)) {
        this.error = "Veuillez entrer un email valide.";
        this.loading = false;
        return;
      }

      try {
        const res = await Api.users.sendDashUserPasswordUrl(email);
        if (res && res.status === "OK") {
          bus.$emit("success", "Un lien de réinitialisation a été envoyé à votre adresse.");
          this.resetMode = false;
        } else {
          const message = (res && res.text) || "Erreur lors de l'envoi du lien de réinitialisation.";
          throw new Error(message);
        }
      } catch (err) {
        this.error = err.message || "Une erreur est survenue.";
        bus.$emit("error", this.error);
      } finally {
        this.loading = false;
      }
    },
    checkLoggedIn() {
      Api.users.profile
        .current()
        .then((res) => {
          if (res.status === "OK") {
            this.username = (res.data.username || "").capitalize();
            this.isSuperAdmin = res.data.role_name === "SUPER_ADMIN";
            this.isBrandPortalUser = res.data.role_name === "PORTAL";

            if (this.username) {
              if (this.isBrandPortalUser) {
                this.openBrandPortal();
              } else if (this.isSuperAdmin) {
                this.openAdmin();
              } else {
                this.openDashboard();
              }
            }
          } else {
            this.username = null;
            this.isSuperAdmin = false;
            this.isBrandPortalUser = false;
          }
        })
        .catch(() => {
          this.username = null;
          this.isSuperAdmin = false;
          this.isBrandPortalUser = false;
        });
    },
    signout() {
      window.location.href = "/signout";
    },
    openDashboard() {
      window.open("/dashboard", "_self");
    },
    openAdmin() {
      window.open("/admin", "_self");
    },
    openBrandPortal() {
      window.open("/brand-portal/home", "_self");
    },
    setLanguage() {
      const lang = navigator.language.slice(0, 2);
      i18n.locale = lang === "fr" ? "fr" : "en";
    },
  },
};

String.prototype.capitalize = function () {
  return this.charAt(0).toUpperCase() + this.slice(1);
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&display=swap");

.login-container {
  min-height: 100vh;
  background-image: url("./assets/warehouse-2.jpeg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.login-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.logo {
  height: 2em;
  width: auto;
  margin-right: 0.2em;
}

h2 {
  font-family: "DM Sans", sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.05em;
  vertical-align: middle;
}

.form-group {
  margin-bottom: 1.25rem;
}

label {
  display: block;
  font-size: 0.9rem;
  color: #374151;
  margin-bottom: 0.25rem;
}

input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  color: #111827;
  background: rgba(255, 255, 255, 0.8);
  transition: border-color 0.3s, box-shadow 0.3s;
}

input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

input::placeholder {
  color: #9ca3af;
}

.error-message {
  color: #dc2626;
  font-size: 0.85rem;
  text-align: center;
  margin-bottom: 1rem;
}

button {
  width: 100%;
  padding: 0.75rem;
  background: linear-gradient(to right, #6366f1, #a855f7);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, background 0.3s;
}

button:hover:not(:disabled) {
  background: linear-gradient(to right, #4f46e5, #9333ea);
  transform: scale(1.05);
}

button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.mt-2 {
  margin-top: 0.5rem;
}

.forgot-password {
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.85rem;
  color: #6b7280;
}

.forgot-password a {
  color: #6366f1;
  text-decoration: none;
}

.forgot-password a:hover {
  text-decoration: underline;
}

@media (max-width: 480px) {
  .login-card {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.5rem;
  }
}
</style>